import { jsxs, jsx } from "react/jsx-runtime";
const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
const STAR_FILL = "#F5B100";
const FG = "var(--voice-widget-fg, #1a1a1a)";
const MUTED = "var(--voice-widget-muted, #6b6b66)";
const SUBMUTED = "#888780";
const BODY = "#444441";
const BORDER = "#e5e5e0";
const CARD_BG = "#ffffff";
const SOFT_BG = "#f7f6f1";
const THEME_PALETTE = {
  positive: { bg: "#E1F5EE", fg: "#085041" },
  caution: { bg: "#FAEEDA", fg: "#633806" },
  negative: { bg: "#FCEBEB", fg: "#791F1F" }
};
const BULLET_PALETTE = {
  positive: { icon: "check", color: "#0F6E56" },
  neutral: { icon: "dot", color: "#888780" },
  caution: { icon: "alert", color: "#854F0B" }
};
function StarIcon({ size = 16, color = STAR_FILL }) {
  return /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: color, "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" }) });
}
function CheckIcon({ size = 16, color }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: 2.5,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("polyline", { points: "20 6 9 17 4 12" })
    }
  );
}
function AlertIcon({ size = 16, color }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }),
        /* @__PURE__ */ jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })
      ]
    }
  );
}
function DotIcon({ size = 16, color }) {
  return /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: color, "aria-hidden": "true", children: /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "3.5" }) });
}
function SearchIcon({ size = 14, color = MUTED }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "11", cy: "11", r: "7" }),
        /* @__PURE__ */ jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })
      ]
    }
  );
}
function BulletIcon({ tone, size = 16 }) {
  const cfg = BULLET_PALETTE[tone];
  if (cfg.icon === "check") return /* @__PURE__ */ jsx(CheckIcon, { size, color: cfg.color });
  if (cfg.icon === "alert") return /* @__PURE__ */ jsx(AlertIcon, { size, color: cfg.color });
  return /* @__PURE__ */ jsx(DotIcon, { size, color: cfg.color });
}
function formatExcerptDate(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  try {
    return new Date(t).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return null;
  }
}
function ExcerptCard({ excerpt }) {
  const dateStr = formatExcerptDate(excerpt.reviewTime);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        border: `0.5px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 6
            },
            children: [
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 }, children: [
                /* @__PURE__ */ jsxs(
                  "span",
                  {
                    style: {
                      fontSize: 12,
                      fontWeight: 500,
                      color: FG,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      flexShrink: 0
                    },
                    children: [
                      /* @__PURE__ */ jsx(StarIcon, { size: 12 }),
                      excerpt.stars.toFixed(1)
                    ]
                  }
                ),
                excerpt.title && /* @__PURE__ */ jsx(
                  "span",
                  {
                    style: {
                      fontSize: 13,
                      fontWeight: 500,
                      color: FG,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    },
                    children: excerpt.title
                  }
                )
              ] }),
              dateStr && /* @__PURE__ */ jsx("span", { style: { fontSize: 11, color: SUBMUTED, flexShrink: 0 }, children: dateStr })
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { style: { fontSize: 13, color: BODY, lineHeight: 1.55 }, children: excerpt.snippet })
      ]
    }
  );
}
function SearchCard({ summary }) {
  const { title, query, totalHits, totalReviews, avgStars, bullets, themes, excerpts } = summary;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        background: CARD_BG,
        border: `0.5px solid ${BORDER}`,
        borderRadius: 12,
        padding: "1.25rem 1.5rem",
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        color: FG,
        animation: "voice-widget-fade-in 0.28s ease-out both"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: "1rem"
            },
            children: [
              /* @__PURE__ */ jsxs("div", { style: { minWidth: 0 }, children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      fontSize: 12,
                      color: MUTED,
                      letterSpacing: "0.02em",
                      marginBottom: 4
                    },
                    children: "Review insight"
                  }
                ),
                /* @__PURE__ */ jsx("h3", { style: { fontSize: 17, fontWeight: 500, margin: 0, color: FG }, children: title }),
                /* @__PURE__ */ jsxs("div", { style: { fontSize: 13, color: "#555550", marginTop: 4 }, children: [
                  totalHits.toLocaleString(),
                  " of ",
                  totalReviews.toLocaleString(),
                  " reviews"
                ] })
              ] }),
              avgStars != null && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0 }, children: [
                /* @__PURE__ */ jsx("span", { style: { fontSize: 26, fontWeight: 500, color: FG }, children: avgStars.toFixed(1) }),
                /* @__PURE__ */ jsx(StarIcon, { size: 16 })
              ] })
            ]
          }
        ),
        query && /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: "10px 12px",
              background: SOFT_BG,
              border: `0.5px solid ${BORDER}`,
              borderRadius: 8,
              marginBottom: "1.25rem"
            },
            children: [
              /* @__PURE__ */ jsx("span", { style: { flexShrink: 0, marginTop: 2 }, children: /* @__PURE__ */ jsx(SearchIcon, {}) }),
              /* @__PURE__ */ jsxs("div", { style: { fontSize: 13, color: BODY, lineHeight: 1.5 }, children: [
                /* @__PURE__ */ jsx("span", { style: { color: MUTED, fontWeight: 500, marginRight: 6 }, children: "Query" }),
                query
              ] })
            ]
          }
        ),
        themes.length > 0 && /* @__PURE__ */ jsxs("div", { style: { marginBottom: "1.25rem" }, children: [
          /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginBottom: 10 }, children: "Key themes" }),
          /* @__PURE__ */ jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 6 }, children: themes.map((t, i) => {
            const palette = THEME_PALETTE[t.tone] ?? THEME_PALETTE.positive;
            return /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: palette.bg,
                  color: palette.fg
                },
                children: t.label
              },
              `${i}-${t.label}`
            );
          }) })
        ] }),
        bullets.length > 0 && /* @__PURE__ */ jsxs("div", { style: { marginBottom: "1.25rem" }, children: [
          /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginBottom: 10 }, children: "What customers said" }),
          /* @__PURE__ */ jsx(
            "ul",
            {
              style: {
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 8
              },
              children: bullets.map((b, i) => /* @__PURE__ */ jsxs(
                "li",
                {
                  style: {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontSize: 14,
                    color: FG,
                    lineHeight: 1.5
                  },
                  children: [
                    /* @__PURE__ */ jsx("span", { style: { marginTop: 2, flexShrink: 0 }, children: /* @__PURE__ */ jsx(BulletIcon, { tone: b.tone }) }),
                    /* @__PURE__ */ jsx("span", { children: b.text })
                  ]
                },
                `${i}-${b.text.slice(0, 12)}`
              ))
            }
          )
        ] }),
        excerpts.length > 0 && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginBottom: 10 }, children: "Excerpts" }),
          /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: 10 }, children: excerpts.map((ex, i) => /* @__PURE__ */ jsx(ExcerptCard, { excerpt: ex }, `${i}-${(ex.title ?? ex.snippet).slice(0, 12)}`)) })
        ] })
      ]
    }
  );
}
const ReviewInsightsWidget = ({ data }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: 680,
        maxHeight: "100%",
        padding: "16px 12px 8px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflowY: "auto"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
        data.searches.map((s) => /* @__PURE__ */ jsx(SearchCard, { summary: s }, s.name))
      ]
    }
  );
};
export {
  ReviewInsightsWidget
};
//# sourceMappingURL=ReviewInsightsWidget.js.map
